'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { useEffect, useState } from 'react';
import Nprogress from 'nprogress';
import { Alert, Button, Form, FormGroup, Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import validateEmailAddress from '../../utils/validateEmailAddress';

import { useUser } from '../../contexts/UserContext';
import { auth } from '../../utils/firebase';
import handleUserLoginOrSignup from '../../utils/handleUserLoginOrSignup';
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter';
import { getI18nUtils } from '../../i18n/i18n-utils';
import { Dictionaries, Locale } from '../../i18n/i18n-config';
import { logSentryError } from '../../utils/logSentry';
import { useParams, usePathname } from 'next/navigation';

type Props = {
  emailAddress?: string;
  dictionary: Partial<Dictionaries>;
};

function SignupWithEmail({ emailAddress, dictionary }: Props): JSX.Element {
  const { locale }: { locale: Locale } = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const emailParams = searchParams.get('email');
  const { formatMessage } = getI18nUtils(dictionary);
  const { candidate } = useUser();
  const [showPassword, setShowpassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const defaultInputs = {
    email: emailParams && typeof emailParams === 'string' ? emailParams : '',
    password: '',
  };
  if (emailAddress) defaultInputs.email = emailAddress;
  const [inputs, setInputs] = useState(defaultInputs);
  const [error, setError] = useState<null | string>(null);
  const [additionalErrorInfo, setAdditionalErrorInfo] = useState<null | string>(null);

  useEffect(() => {
    if (!emailAddress && candidate?.email) setInputs((prev) => ({ ...prev, email: candidate.email }));
  }, [emailAddress, candidate]);

  const { email, password } = inputs;
  const formValid = !!(validateEmailAddress(email) && password !== '');

  const toggleViewPassword = () => setShowpassword((prev) => !prev);

  async function submitHandler(e) {
    e.preventDefault();
    setError(null);
    setAdditionalErrorInfo(null);
    Nprogress.start();
    setLoading(true);
    try {
      // @info: signin will trigger onIdTokenChanged in usercontext
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await handleUserLoginOrSignup({ userCredential, locale, pathname, router }); // redirect user
    } catch (error) {
      setLoading(false);
      let errorMessage = 'internal-server-error';

      switch (error?.code) {
        case 'auth/email-already-in-use':
        case 'auth/email-already-exists':
          errorMessage = 'signup-error-email-in-use';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'login-error-too-many-request';
          break;
        case 'auth/weak-password':
          errorMessage = 'login-error-weak-password';
          break;
        case 'auth/invalid-password':
          errorMessage = 'login-error-invalid-password';
          if (error?.response?.data?.message) setAdditionalErrorInfo(error.response.data.message);
          break;
        default:
          logSentryError(error);
          break;
      }
      setError(errorMessage);
    }

    Nprogress.done();
    setLoading(false);
  }

  function handleInputChange({ target: { name, value } }) {
    setInputs({ ...inputs, [name]: value });
  }

  return (
    <Form onSubmit={(e) => submitHandler(e)} autoComplete="on" id="login-with-email">
      <fieldset disabled={loading}>
        <FormGroup className="form-group position-relative">
          <Label>E-mail</Label>
          <Input
            className="w-100"
            type="email"
            name="email"
            id="email-signup"
            autoComplete="email"
            required
            placeholder="E-mail"
            value={email}
            valid={validateEmailAddress(email)}
            onChange={(e) => handleInputChange(e)}
          />
        </FormGroup>

        <FormGroup className="form-group position-relative">
          <Label>{formatMessage({ id: 'password' })}</Label>
          <InputGroup>
            <Input
              // className="w-100"
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              placeholder={capitalizeFirstLetter(
                formatMessage({ id: 'item-pick' }, { item: formatMessage({ id: 'password' }) }, true)
              )}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => handleInputChange(e)}
            />
            <InputGroupText onClick={toggleViewPassword}>
              {showPassword ? <FaEyeSlash color="#ADB5BD" size={20} /> : <FaEye color="#ADB5BD" size={20} />}
            </InputGroupText>
          </InputGroup>
        </FormGroup>

        {error && (
          <Alert color="danger" className="py-2">
            <div className="d-flex flex-row align-items-start">
              <div className="me-2">⚠️</div>
              <div>
                <div>
                  {formatMessage({ id: error })}
                  {additionalErrorInfo && <span>{` - ${formatMessage({ id: additionalErrorInfo })}`}</span>}
                </div>
                {error === 'signup-error-email-in-use' && (
                  <Link href={`/${locale}/auth/login/`} className="d-block" style={{ color: 'inherit' }}>
                    {formatMessage({ id: 'login' })}
                  </Link>
                )}
              </div>
            </div>
          </Alert>
        )}

        <Button className="w-100 pt-2" color="primary" type="submit" disabled={!formValid}>
          {formatMessage({ id: 'login-createUser' })}
        </Button>
      </fieldset>
    </Form>
  );
}

export default SignupWithEmail;
